import Vue from 'vue'
import Vuex from 'vuex'
import http from '@/utils/request'

Vue.use(Vuex)

function normalizeCategories(categories) {
  const list = (categories || []).map(cat => ({
    ...cat,
    id: parseInt(cat.id, 10) || 0,
    sort: parseInt(cat.sort, 10) || 0,
    kind: cat.kind || '',
    sub_categories: (cat.sub_categories || []).map(sub => ({
      ...sub,
      id: parseInt(sub.id, 10) || 0,
      sort: parseInt(sub.sort, 10) || 0,
      question_count: parseInt(sub.question_count, 10) || 0
    })).sort((a, b) => (a.sort !== b.sort ? a.sort - b.sort : a.id - b.id))
  }))
  list.sort((a, b) => (a.sort !== b.sort ? a.sort - b.sort : a.id - b.id))
  return list
}

export default new Vuex.Store({
  state: {
    token: localStorage.getItem('token') || '',
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    categories: [],
    currentCategory: null,
    currentSubCategory: null,
    currentBankId: 0,
    questions: [],
    currentQuestionIndex: 0,
    answers: [],
    totalScore: 0,
    userProgress: {},
    previousRoute: null,
    quizHideTabbar: false
  },
  mutations: {
    SET_TOKEN(state, token) {
      state.token = token
      localStorage.setItem('token', token)
    },
    SET_USER(state, user) {
      state.user = user
      localStorage.setItem('user', JSON.stringify(user))
    },
    SET_CATEGORIES(state, categories) {
      state.categories = categories
    },
    SET_CURRENT_CATEGORY(state, category) {
      state.currentCategory = category
    },
    SET_CURRENT_SUB_CATEGORY(state, subCategory) {
      state.currentSubCategory = subCategory
    },
    SET_CURRENT_BANK_ID(state, bankId) {
      state.currentBankId = parseInt(bankId, 10) || 0
    },
    SET_QUESTIONS(state, questions) {
      state.questions = questions
    },
    SET_CURRENT_QUESTION_INDEX(state, index) {
      state.currentQuestionIndex = index
    },
    ADD_ANSWER(state, answer) {
      state.answers.push(answer)
    },
    SET_TOTAL_SCORE(state, score) {
      state.totalScore = score
    },
    SET_USER_PROGRESS(state, progress) {
      state.userProgress = progress
    },
    SET_PREVIOUS_ROUTE(state, route) {
      state.previousRoute = route
    },
    SET_QUIZ_HIDE_TABBAR(state, hidden) {
      state.quizHideTabbar = !!hidden
    },
    CLEAR_QUIZ(state) {
      state.currentQuestionIndex = 0
      state.answers = []
      state.totalScore = 0
      state.currentCategory = null
      state.currentSubCategory = null
    },
    RESET_SESSION_ANSWERS(state) {
      state.answers = []
      state.currentQuestionIndex = 0
      state.totalScore = 0
    },
    LOGOUT(state) {
      state.token = ''
      state.user = null
      state.categories = []
      state.currentCategory = null
      state.currentSubCategory = null
      state.questions = []
      state.currentQuestionIndex = 0
      state.answers = []
      state.totalScore = 0
      state.userProgress = {}
      localStorage.removeItem('token')
      localStorage.removeItem('user')
    }
  },
  actions: {
    async login({ commit }, code) {
      const response = await http.post('/api/wx/login', { code })
      if (response.data.code === 200) {
        commit('SET_TOKEN', response.data.data.token)
        commit('SET_USER', response.data.data.user)
        return response.data.data
      }
      throw new Error(response.data.msg)
    },
    async fetchUserInfo({ commit, state }) {
      const response = await http.get('/api/wx/userinfo')
      if (response.data.code === 200) {
        commit('SET_USER', response.data.data)
        return response.data.data
      }
      throw new Error(response.data.msg)
    },
    async fetchQuestionBanks({ commit }) {
      const response = await http.get('/api/question-banks')
      if (response.data.code === 200) {
        const data = response.data.data || {}
        return {
          banks: data.banks || [],
          maxOwnedLevel: data.max_owned_level || 0,
          bankCount: data.bank_count || 0
        }
      }
      throw new Error(response.data.msg)
    },
    async fetchCategories({ commit, state }, bankId = 0) {
      const params = bankId > 0 ? { bank_id: bankId } : {}
      const response = await http.get('/api/categories', { params })
      if (response.data.code === 200) {
        const responseData = response.data.data || {}
        const categories = normalizeCategories(responseData.categories || [])
        commit('SET_CATEGORIES', categories)
        return categories
      }
      throw new Error(response.data.msg)
    },
    async fetchUserProgress({ commit, state }) {
      const response = await http.get('/api/progress')
      if (response.data.code === 200) {
        commit('SET_USER_PROGRESS', response.data.data || {})
        return response.data.data || {}
      }
      throw new Error(response.data.msg)
    },
    async fetchQuestions({ commit }, payload = 0) {
      const params = {}
      if (payload && typeof payload === 'object') {
      if (payload.categoryId > 0) params.category_id = payload.categoryId
      if (payload.bankId > 0) params.bank_id = payload.bankId
      if (payload.subCategoryId > 0) params.sub_category_id = payload.subCategoryId
      } else if (payload > 0) {
        params.sub_category_id = payload
      }
      const response = await http.get('/api/question/list', { params })
      if (response.data.code === 200) {
        commit('SET_QUESTIONS', response.data.data || [])
        return response.data.data || []
      }
      throw new Error(response.data.msg)
    },
    async submitAnswer({ commit, state }, { questionId, answer }) {
      const response = await http.post('/api/answer/submit', {
        question_id: questionId,
        answer: answer
      })
      if (response.data.code === 200) {
        const result = response.data.data
        const q = state.questions.find(item => item.id === questionId)
        commit('ADD_ANSWER', {
          questionId,
          subCategoryId: q ? q.sub_category_id : null,
          answer,
          isCorrect: result.is_correct,
          score: result.score,
          isAudio: result.is_audio
        })
        if (!result.is_audio && result.total_score !== undefined && result.total_score !== null) {
          commit('SET_TOTAL_SCORE', result.total_score)
        }
        if (state.user) {
          const u = { ...state.user }
          if (result.answer_count !== undefined && result.answer_count !== null) {
            u.answer_count = result.answer_count
          }
          if (result.total_score !== undefined && result.total_score !== null) {
            u.total_score = result.total_score
          }
          if (result.accuracy_percent !== undefined && result.accuracy_percent !== null) {
            u.accuracy_percent = result.accuracy_percent
          }
          commit('SET_USER', u)
        }
        return result
      }
      throw new Error(response.data.msg)
    },
    async startQuiz({ dispatch, commit, state }, options = {}) {
      const bankId = options.bankId ? parseInt(options.bankId, 10) : 0
      if (bankId > 0) {
        commit('SET_CURRENT_BANK_ID', bankId)
      }
      const categories = await dispatch('fetchCategories', bankId)
      
      // 按大类ID排序，然后按小类ID排序
      categories.sort((a, b) => a.id - b.id)
      categories.forEach(category => {
        category.sub_categories.sort((a, b) => a.id - b.id)
      })
      
      // 获取用户答题进度（如果用户未登录，返回空对象）
      let progress = {}
      try {
        progress = await dispatch('fetchUserProgress')
      } catch (err) {
        console.log('用户未登录或获取进度失败，使用默认进度')
        progress = {}
      }
      
      const hasQuestions = (sub) => (sub.question_count || 0) > 0
      const isSubIncomplete = (subId) => {
        const subProgress = progress[subId]
        return !(subProgress && subProgress.completed)
      }

      let targetCategory = null
      let targetSubCategory = null

      // 优先：第一个含有「未完成小类」的大类
      for (const category of categories) {
        const subs = category.sub_categories || []
        const hasOpen = subs.some(sub => hasQuestions(sub) && isSubIncomplete(sub.id))
        if (hasOpen) {
          targetCategory = category
          targetSubCategory = subs.find(sub => hasQuestions(sub) && isSubIncomplete(sub.id)) || subs[0]
          break
        }
      }

      // 若全部标记完成，则从第一个有题的大类重新开始
      if (!targetCategory) {
        for (const category of categories) {
          const subs = (category.sub_categories || []).filter(hasQuestions)
          if (subs.length > 0) {
            targetCategory = category
            targetSubCategory = subs[0]
            break
          }
        }
      }

      if (!targetCategory) {
        throw new Error('暂无可用题目，请确认后台已上传题目且分类已启用')
      }

      commit('SET_CURRENT_CATEGORY', targetCategory)
      commit('SET_CURRENT_SUB_CATEGORY', targetSubCategory)
      commit('RESET_SESSION_ANSWERS')

      // 按「大类」拉取题目，一次答完该分类下各小类
      const fetchPayload = { categoryId: targetCategory.id }
      if (bankId > 0) fetchPayload.bankId = bankId
      const questions = await dispatch('fetchQuestions', fetchPayload)

      if (!questions || questions.length === 0) {
        throw new Error('该分类下暂无题目，请换一个分类或联系管理员')
      }

      return { category: targetCategory, subCategory: targetSubCategory, questionCount: questions.length }
    },
    async startSubCategoryQuiz({ dispatch, commit, state }, options = {}) {
      const bid = parseInt(options.bankId, 10) || 0
      const cid = parseInt(options.categoryId, 10) || 0
      const sid = parseInt(options.subCategoryId, 10) || 0
      const subName = options.subCategoryName ? String(options.subCategoryName) : ''
      const categoryName = options.categoryName ? String(options.categoryName) : ''
      if (sid <= 0) {
        throw new Error('请选择训练项')
      }
      if (bid > 0) {
        commit('SET_CURRENT_BANK_ID', bid)
      }
      const categories = await dispatch('fetchCategories', bid)
      let targetCategory = categories.find(c => c.id === cid) || null
      let targetSub = targetCategory
        ? (targetCategory.sub_categories || []).find(s => s.id === sid)
        : null
      if (!targetSub) {
        for (const category of categories) {
          const sub = (category.sub_categories || []).find(s => s.id === sid)
          if (sub) {
            targetCategory = category
            targetSub = sub
            break
          }
        }
      }
      const fetchPayload = { subCategoryId: sid }
      if (bid > 0) fetchPayload.bankId = bid
      const questions = await dispatch('fetchQuestions', fetchPayload)
      if (!questions || questions.length === 0) {
        if (!targetSub) {
          throw new Error('训练项不存在或已下架')
        }
        throw new Error('该训练项暂无题目')
      }
      if (!targetCategory) {
        targetCategory = {
          id: cid || 0,
          name: categoryName || '训练分类',
          sub_categories: []
        }
      }
      if (!targetSub) {
        targetSub = {
          id: sid,
          name: subName || '单项训练',
          question_count: questions.length
        }
      }
      commit('SET_CURRENT_CATEGORY', targetCategory)
      commit('SET_CURRENT_SUB_CATEGORY', targetSub)
      commit('RESET_SESSION_ANSWERS')
      return {
        category: targetCategory,
        subCategory: targetSub,
        questionCount: questions.length
      }
    }
  },
  getters: {
    isLoggedIn: state => !!state.token,
    isVip: state => {
      if (!state.user) return false
      const level = parseInt(state.user.vip_level, 10)
      if (!level) return false
      const endTime = parseInt(state.user.vip_end_time, 10)
      // 0 / 未设置 / 2147483647 均视为永久 VIP
      if (isNaN(endTime) || endTime === 0 || endTime >= 2147483647) return true
      return endTime > Math.floor(Date.now() / 1000)
    },
    currentQuestion: state => state.questions[state.currentQuestionIndex] || null,
    progress: state => {
      const totalCount = state.questions.length || 1
      if (totalCount === 0) return 0
      return Math.round(((state.currentQuestionIndex + 1) / totalCount) * 100)
    },
    sessionQuestionCount: state => state.questions.length,
    currentCategoryName: state => state.currentCategory ? state.currentCategory.name : '',
    currentSubCategoryName: state => state.currentSubCategory ? state.currentSubCategory.name : '',
    smartBackRoute: state => {
      if (!state.previousRoute) {
        return '/home'
      }
      
      const fromPath = state.previousRoute.path
      const fromName = state.previousRoute.name
      
      if (fromName === 'Home' || fromPath === '/home') {
        return '/home'
      }

      if (fromName === 'TrainingList' || fromPath === '/training') {
        return '/training'
      }
      
      if (fromName === 'Profile' || fromPath === '/profile') {
        return '/profile'
      }
      
      if (fromName === 'Records' || fromPath === '/records') {
        return '/records'
      }
      
      if (fromName === 'Result' || fromPath === '/result') {
        return '/result'
      }
      
      if (fromName === 'Vip' || fromPath === '/vip') {
        return '/vip'
      }
      
      if (fromName === 'RecordDetail' || fromPath.startsWith('/record/')) {
        return '/records'
      }
      
      return fromPath
    }
  }
})